// src/features/system/devices/components/iot/IoTTelemetryPanel.tsx
// ────────────────────────────────────────────────────────────────────────────
// Displays IoT telemetry for all devices linked to the active tenant.
// All tenant devices are auto-mixed — each device is an overlay in the
// strip-chart.  Labels are shown everywhere; device_id is internal only.
//
// Uses the Telemetry Streaming Engine:
//   1. CACHE  — instant: cached points shown immediately on time-window switch
//   2. STREAM — progressive: binary ReadableStream cascades missing ranges
//   3. DONE   — all data loaded, 15s poll re-arms for live tail
//
// No blank screen, no spinner on time-window changes.  Data cascade fills
// in like a cardiogram / seismograph — existing data stays, new data
// streams in from the edges.
//
// Uses shared telemetry factory components from @/shared/telemetry.
// ────────────────────────────────────────────────────────────────────────────
import React, { useMemo, useRef, useState, useCallback, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import MinimalisticTable, { type ColumnDef } from '@/shared/components/ui/MinimalisticTable';
import PresenceDot from '@/shared/components/ui/PresenceDot';
import type { TimeSeriesPoint } from '@/shared/components/plots/TelemetryTimeSeriesPlot';
import { applyDsp } from '@/shared/dsp/telemetryDsp';
import DspControlPanel, { useDspPreset } from './DspControlPanel';
import ShareViewDialog from './ShareViewDialog';
import { useTenantOverride } from '@/shared/auth/tenantContext';
import { useRbac } from '@/shared/auth/useRbac';
import {
  fetchPresenceApi,
  fetchTelemetrySnapshotApi,
  fetchDeviceStatsApi,
  renameDeviceApi,
  DEVICE_KEYS,
  type Device,
  type DevicePresence,
  type TelemetrySnapshot,
  type TelemetryTableRow,
  type DeviceStat,
} from '../../api/iotDevicesApi';
import { useDevicesList } from '../../hooks/useDevicesList';
import { useTelemetryStreamers } from '../../hooks/useTelemetryStreamers';
import { useTelemetryStream, type MetricKey } from '@/shared/streaming';
import StreamerManagerDialog from './StreamerManagerDialog';
import {
  KpiTile,
  MagnitudeCard,
  TelemetryErrorBanner,
  StreamProgress,
  TimeWindowPills,
  SignalStatsTable,
  computeStats,
  fmtStat,
  timeAgo,
  formatTsFull,
  fmtSpan,
  type TimeWindow,
  type MetricGroup,
  type DeviceSignalStat,
  type MagnitudeDefinition,
} from '@/shared/telemetry';

/* ── Constants ───────────────────────────────────────────────────────── */

type SubView = 'Plots' | 'Table' | 'Stats';
const SUB_VIEWS: SubView[] = ['Plots', 'Table', 'Stats'];

/** Device types that produce IoT sensor telemetry (temp / hum / rssi). */
const IOT_SENSOR_TYPES = new Set(['esp32', 'shelly']);

/** Time-window presets for the strip-chart and BFF query.
 *  `limit` is the per-scope row cap sent to the BFF/orchestrator LATERAL
 *  query — each scope_key gets up to `limit` rows from the DB within the
 *  requested [from, to] range.  BFF further caps at lttbTarget×4 and
 *  applies LTTB downsampling to 400–800 pts/metric/scope.
 *
 *  Limits are right-sized to actual data density (~6 readings/min × 3
 *  metrics = ~1080 rows/scope/hour).  Over-requesting wastes DB scans.
 *
 *  For windows ≤1h, cascade is skipped — single fetch returns instantly.
 *  For larger windows, cascade steps use coarse expanding sweeps.     */
const TIME_WINDOWS: TimeWindow[] = [
  { label: '1m',   sec:       60, limit:    200 },
  { label: '5m',   sec:      300, limit:    400 },
  { label: '10m',  sec:      600, limit:    800 },
  { label: '30m',  sec:    1_800, limit:  1_200 },
  { label: '1h',   sec:    3_600, limit:  1_600 },
  { label: '6h',   sec:   21_600, limit:  2_000 },
  { label: '1d',   sec:   86_400, limit:  2_500 },
  { label: '1w',   sec:  604_800, limit:  3_000 },
];
const DEFAULT_WINDOW = TIME_WINDOWS[4]; // 1h

/* ── Extended table row for mixed-device table view ──────────────────── */
interface MixedTableRow extends TelemetryTableRow {
  deviceLabel: string;
}

/** Readable device label — prefers entity label, falls back to short ID suffix. */
function deviceLabel(dev: Device): string {
  if (dev.label) return dev.label;
  // Extract meaningful suffix from device_id (typically MAC-based like pac-iot-b0cbd8cdfee4)
  const id = dev.device_id;
  const short = id.length > 8 ? id.slice(-8) : id;
  return short;
}

/** Compact number formatter: 73152 → "73.2k", 1234 → "1.2k", 980 → "980". */
function fmtCompact(n: number): string {
  if (n < 1_000) return String(n);
  if (n < 10_000) return `${(n / 1_000).toFixed(1)}k`;
  if (n < 1_000_000) return `${Math.round(n / 1_000)}k`;
  return `${(n / 1_000_000).toFixed(1)}M`;
}

/* ── Component ───────────────────────────────────────────────────────── */

/** 1-second heartbeat for live timeAgo() updates across all KPI tiles and cards. */
function useTick(intervalMs = 1_000) {
  const [, setTick] = useState(0);
  useEffect(() => {
    const id = setInterval(() => setTick(t => t + 1), intervalMs);
    return () => clearInterval(id);
  }, [intervalMs]);
}

export default function IoTTelemetryPanel() {
  useTick(1_000);
  const { activeTenantId } = useTenantOverride();
  const { isSuperadmin } = useRbac();
  const queryClient = useQueryClient();
  const [metricFilter, setMetricFilter] = useState<string>('');
  const [subView, setSubView]           = useState<SubView>('Plots');
  const [timeWindow, setTimeWindow]     = useState<TimeWindow>(DEFAULT_WINDOW);
  const [selectedDeviceId, setSelectedDeviceId] = useState<string | null>(null);
  const [shareOpen, setShareOpen] = useState(false);
  const [streamerMgrOpen, setStreamerMgrOpen] = useState(false);

  /** Cross-plot hover sync — all three strip-charts share one timestamp ref. */
  const hoverTsRef = useRef<number | null>(null);

  /* DSP preset state (persisted to localStorage) */
  const { preset: dspPreset, enabled: dspEnabled, setPreset: setDspPreset, setEnabled: setDspEnabled } = useDspPreset();

  /* ── Rename mutation ─────────────────────────────────────────────── */
  const renameMutation = useMutation({
    mutationFn: ({ deviceId, label }: { deviceId: string; label: string }) =>
      renameDeviceApi(deviceId, label),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: DEVICE_KEYS.list });
    },
  });
  const handleRename = useCallback(
    (deviceId: string, label: string) => renameMutation.mutate({ deviceId, label }),
    [renameMutation],
  );

  /* All devices (BFF already scopes by tenant header) */
  const { data: allDevices = [], isFetching: devicesFetching } = useDevicesList();

  /* Filter to active IoT sensor devices belonging to the current tenant.
   * Excludes non-sensor entities (bff-service, fe-peer, shared-link, etc.)
   * that share the device registry but don't produce IoT telemetry.       */
  const tenantDevices = useMemo(
    () => allDevices.filter(d =>
      d.tenant_id === activeTenantId &&
      d.is_active &&
      (IOT_SENSOR_TYPES.has(d.device_type ?? '') || d.device_id.startsWith('pac-iot-'))
    ),
    [allDevices, activeTenantId],
  );
  const tenantDeviceIds = useMemo(
    () => tenantDevices.map(d => d.device_id),
    [tenantDevices],
  );

  /* ── Streamer selection (PACDaemon CLI-style opt-out) ─────────────
   *  Gates which devices reach useTelemetryStream / mux-bin.
   *  Persisted to localStorage per tenant.                           */
  const streamers = useTelemetryStreamers(tenantDevices, activeTenantId ?? undefined);

  const hasDevices = tenantDeviceIds.length > 0;

  /* ── Presence + snapshot queries (KPI header data) ───────────────── */
  const { data: presenceList = [] } = useQuery<DevicePresence[]>({
    queryKey: DEVICE_KEYS.presence,
    queryFn:  fetchPresenceApi,
    refetchInterval: 15_000,
    staleTime: 10_000,
  });

  const { data: snapshots = [] } = useQuery<TelemetrySnapshot[]>({
    queryKey: DEVICE_KEYS.snapshot,
    queryFn:  fetchTelemetrySnapshotApi,
    refetchInterval: 30_000,
    staleTime: 15_000,
    enabled:  hasDevices,
  });

  /* ── Device selection filter (click KPI card → drill-down) ───────── */
  const displayDevices = useMemo(
    () => selectedDeviceId
      ? tenantDevices.filter(d => d.device_id === selectedDeviceId)
      : tenantDevices,
    [tenantDevices, selectedDeviceId],
  );

  /* ── Adaptive refresh interval ────────────────────────────────────────
   *  Tiny windows  (≤1m):   5s — near-real-time tail, smooth cascade.
   *  Small windows (≤5m):   8s — live tail, delta polls ~8s of new data.
   *  Medium windows (≤10m): 12s — balance freshness vs DB pressure.
   *  Medium-large  (≤1h):   30s — data changes are subtle at this scale.
   *  Large windows  (>1h):  90s — new data is sub-pixel at chart resolution.
   *  Combined with delta coverage, polls fetch only the right-edge gap.  */
  const refreshInterval = useMemo(() => {
    if (timeWindow.sec <= 60)    return 5_000;   // ≤1m  → 5s
    if (timeWindow.sec <= 300)   return 8_000;   // ≤5m  → 8s
    if (timeWindow.sec <= 600)   return 12_000;  // ≤10m → 12s
    if (timeWindow.sec <= 3_600) return 30_000;  // ≤1h  → 30s
    return 90_000;                                // >1h  → 90s
  }, [timeWindow.sec]);

  /* ── Streaming telemetry engine ─────────────────────────────────────
   *  Replaces the old useQuery fetch-and-wait pattern.
   *  On time-window switch: cached data stays → delta streams in.       */
  const {
    byDevice: streamByDevice,
    phase,
    progress,
    error: streamError,
    fault: streamFault,
    isLoading,
    isRefreshing,
  } = useTelemetryStream({
    deviceIds: streamers.enabledIds,
    windowSec: timeWindow.sec,
    limit:     timeWindow.limit ?? 2_000,
    refreshInterval,
    enabled:   hasDevices && streamers.enabledCount > 0,
  });

  /* ── Per-device ingestion stats (BFF-digested, 60s poll) ────────────
   *  LAZY — only fetched when the Stats sub-view is active.
   *  Prevents cold-start cascade: initial load only fires streaming +
   *  presence + snapshot.  Stats is heavy (raw row scan on orchestrator),
   *  so we defer it until the user actually opens the Stats tab.         */
  const statsFrom = useMemo(
    () => new Date(Date.now() - timeWindow.sec * 1_000).toISOString(),
    [timeWindow.sec],
  );
  const { data: deviceStats } = useQuery({
    queryKey: DEVICE_KEYS.stats(timeWindow.label),
    queryFn:  () => fetchDeviceStatsApi({
      scope_ns: 'iot',
      from:     statsFrom,
      limit:    timeWindow.limit ?? 5_000,
    }),
    refetchInterval: 60_000,
    enabled: hasDevices && subView === 'Stats',
    staleTime: 30_000,
  });

  /* ── Palette + overlay builder ─────────────────────────────────────── */
  const palette = ['#34d399', '#60a5fa', '#f97316', '#f43f5e', '#a78bfa', '#f59e0b'];

  const buildOverlays = (metric: MetricKey) =>
    displayDevices.map((dev) => {
      const globalIdx = tenantDevices.findIndex(d => d.device_id === dev.device_id);
      const raw = streamByDevice[dev.device_id]?.[metric as MetricKey] ?? [];
      const data = dspEnabled ? applyDsp(raw, dspPreset) : raw;
      return {
        label: deviceLabel(dev),
        data,
        color: palette[globalIdx % palette.length],
      };
    });

  /* ── Grouped signal stats across ALL tenant devices ─────────────────
   *  Always uses tenantDevices (not displayDevices) so the table shows
   *  every device regardless of the KPI-card selection filter.          */
  const METRIC_DEFS: { key: MetricKey; metric: string; unit: string; color: string }[] = [
    { key: 'temp', metric: 'Temperature', unit: '°C',  color: '#34d399' },
    { key: 'hum',  metric: 'Humidity',    unit: '%RH', color: '#60a5fa' },
    { key: 'rssi', metric: 'RSSI',        unit: 'dBm', color: '#f97316' },
  ];

  const metricGroups = useMemo<MetricGroup[]>(() => {
    return METRIC_DEFS.map(md => {
      const sourceRows: DeviceSignalStat[] = [];
      let allMin = Infinity, allMax = -Infinity, allSum = 0, allCount = 0;

      for (let i = 0; i < tenantDevices.length; i++) {
        const dev = tenantDevices[i];
        const raw = streamByDevice[dev.device_id]?.[md.key] ?? [];
        const pts = dspEnabled ? applyDsp(raw, dspPreset) : raw;
        const s = computeStats(pts);
        if (s) {
          sourceRows.push({
            label: deviceLabel(dev),
            color: palette[i % palette.length],
            ...s,
          });
          if (s.min < allMin) allMin = s.min;
          if (s.max > allMax) allMax = s.max;
          allSum += s.avg * pts.length;
          allCount += pts.length;
        }
      }

      if (sourceRows.length === 0) return null;
      return {
        metric: md.metric,
        unit:   md.unit,
        color:  md.color,
        min:    allMin,
        max:    allMax,
        delta:  allMax - allMin,
        avg:    allCount > 0 ? allSum / allCount : 0,
        sources: sourceRows,
      };
    }).filter(Boolean) as MetricGroup[];
  }, [streamByDevice, tenantDevices, dspPreset, dspEnabled, palette]);

  /* ── Merged table rows (derived from stream cache) ───────────────── */
  const METRIC_META: { key: MetricKey; label: string; unit: string }[] = [
    { key: 'temp', label: 'Temperature', unit: '°C'  },
    { key: 'hum',  label: 'Humidity',    unit: '%RH' },
    { key: 'rssi', label: 'RSSI',        unit: 'dBm' },
  ];

  const mergedTable = useMemo<MixedTableRow[]>(() => {
    const rows: MixedTableRow[] = [];
    for (const dev of displayDevices) {
      const devData = streamByDevice[dev.device_id];
      if (!devData) continue;
      const label = deviceLabel(dev);
      for (const mm of METRIC_META) {
        const pts = devData[mm.key] ?? [];
        for (const p of pts) {
          if (!p || typeof p.ts !== 'number') continue;
          rows.push({
            ts:          new Date(p.ts).toISOString(),
            metric:      mm.label,
            value:       p.value,
            unit:        mm.unit,
            deviceLabel: label,
          });
        }
      }
    }
    rows.sort((a, b) => (b.ts > a.ts ? 1 : b.ts < a.ts ? -1 : 0));
    return rows;
  }, [streamByDevice, displayDevices]);

  /* Unique metric labels for the filter dropdown (deduplicated) */
  const metricLabels = useMemo(
    () => Array.from(new Set(mergedTable.map(r => r.metric))).sort(),
    [mergedTable],
  );

  /* Filtered table rows */
  const filteredTable = useMemo(
    () => metricFilter ? mergedTable.filter(r => r.metric === metricFilter) : mergedTable,
    [mergedTable, metricFilter],
  );

  /* Table columns — label + time + metric + value, no device_id */
  const columns: ColumnDef<MixedTableRow>[] = [
    {
      header: 'Device',
      accessor: 'deviceLabel' as keyof MixedTableRow,
      Cell: (v: string) => <span className="text-xs text-neutral-300">{v}</span>,
    },
    {
      header: 'Time',
      accessor: 'ts',
      Cell: (v: string) => (
        <span className="text-xs cursor-default" title={formatTsFull(v)}>{timeAgo(v)}</span>
      ),
    },
    {
      header: 'Metric',
      accessor: 'metric',
      Cell: (v: string) => <span className="font-mono text-xs text-sky-400">{v}</span>,
    },
    {
      header: 'Value',
      accessor: 'value',
      Cell: (v: number, row: MixedTableRow) => (
        <span className="font-mono text-xs">{v}{row.unit ? ` ${row.unit}` : ''}</span>
      ),
    },
  ];

  return (
    <div className="h-full flex flex-col gap-3 overflow-hidden">
      {/* ── Controls row ──────────────────────────────────────────── */}
      <div className="flex items-center gap-3 shrink-0 flex-wrap">
        <span className="text-xs text-neutral-400">
          {hasDevices
            ? `${streamers.enabledCount}/${tenantDevices.length} streamer${streamers.enabledCount !== 1 ? 's' : ''} ·`
            : devicesFetching ? 'Loading devices…' : 'No IoT devices linked to this tenant'}
        </span>
        {hasDevices && (
          <button
            onClick={() => setStreamerMgrOpen(true)}
            className="text-xs text-neutral-500 hover:text-emerald-400
              transition-colors cursor-pointer"
            title="Manage telemetry streamers"
          >
            {tenantDevices
              .filter(d => streamers.isEnabled(d.device_id))
              .map(d => deviceLabel(d))
              .join(', ') || 'none selected'}
          </button>
        )}

        {/* Metric filter — Table view only */}
        {hasDevices && subView === 'Table' && metricLabels.length > 0 && (
          <>
            <label className="text-xs text-neutral-400 ml-2">Filter:</label>
            <select
              value={metricFilter}
              onChange={e => setMetricFilter(e.target.value)}
              className="px-2 py-1 rounded pac-glass-input text-neutral-100 text-sm
                         focus:outline-none focus:ring-1 focus:ring-sky-500"
            >
              <option value="">All metrics</option>
              {metricLabels.map(m => (
                <option key={m} value={m}>{m}</option>
              ))}
            </select>
          </>
        )}

        {/* Share button */}
        {hasDevices && (
          <button
            onClick={() => setShareOpen(true)}
            className="ml-auto px-3 py-1 rounded-md text-xs font-medium tracking-wide
              bg-white/[0.06] text-neutral-400 hover:text-emerald-400 hover:bg-white/[0.10]
              border border-white/[0.06] hover:border-emerald-500/30
              transition-all duration-200"
            title="Generate shareable URL"
          >
            Share
          </button>
        )}

        {isRefreshing && <span className="text-xs text-neutral-600 animate-pulse">refreshing…</span>}
        {phase === 'streaming' && (
          <StreamProgress progress={progress} />
        )}
      </div>

      {/* ── Device Fleet KPI Header ───────────────────────────────── */}
      {hasDevices && (
        <DeviceFleetKpi
          devices={tenantDevices}
          presenceList={presenceList}
          snapshots={snapshots}
          streamByDevice={streamByDevice}
          selectedDeviceId={selectedDeviceId}
          onSelectDevice={setSelectedDeviceId}
          onRename={handleRename}
          palette={palette}
          timeWindowLabel={timeWindow.label}
          isSuperadmin={isSuperadmin}
        />
      )}

      {/* ── DSP Control Panel ─────────────────────────────────────── */}
      {hasDevices && (
        <DspControlPanel
          preset={dspPreset}
          enabled={dspEnabled}
          onChange={setDspPreset}
          onToggle={setDspEnabled}
        />
      )}

      {/* ── Sub-tab bar: Plots | Table  +  time-window toggle ─────── */}
      {hasDevices && (
        <div className="shrink-0 flex items-center border-b border-white/[0.06]">
          {SUB_VIEWS.map(tab => (
            <button
              key={tab}
              onClick={() => setSubView(tab)}
              className={
                `pac-glass-tab px-4 py-1.5 text-xs tracking-wide uppercase flex-shrink-0
                ${tab === subView ? 'pac-tab-active font-semibold' : 'text-neutral-500'}`
              }
            >
              {tab}
            </button>
          ))}

          {/* spacer */}
          <div className="flex-1" />

          {/* Time-window toggle — segmented bar */}
          <TimeWindowPills
            windows={TIME_WINDOWS}
            active={timeWindow}
            onChange={setTimeWindow}
            variant="bar"
          />
        </div>
      )}

      {/* ── Streamer Manager dialog ───────────────────────────────── */}
      <StreamerManagerDialog
        open={streamerMgrOpen}
        onOpenChange={setStreamerMgrOpen}
        streamers={streamers}
        presenceList={presenceList}
        palette={palette}
      />

      {/* ── Share dialog ──────────────────────────────────────────── */}
      <ShareViewDialog
        open={shareOpen}
        onOpenChange={setShareOpen}
        devices={tenantDevices}
        deviceLabel={deviceLabel}
        timeWindow={timeWindow.label}
        palette={palette}
      />

      {/* ── Placeholder ───────────────────────────────────────────── */}
      {!hasDevices && devicesFetching && (
        <div className="flex-1 grid place-items-center text-neutral-500 text-sm animate-pulse">
          Loading devices…
        </div>
      )}
      {!hasDevices && !devicesFetching && (
        <div className="flex-1 grid place-items-center text-neutral-600 text-sm">
          No IoT devices linked to this tenant.
        </div>
      )}

      {/* ── Plots view — interleaved: stats table → plot per metric ── */}
      {hasDevices && subView === 'Plots' && (
        <div className="flex-1 min-h-0 overflow-y-auto flex flex-col gap-3 pr-1">
          {streamError && (
            <TelemetryErrorBanner fault={streamFault} message={streamError} />
          )}
          {!streamError && phase === 'idle' && (
            <div className="text-center text-neutral-600 text-xs animate-pulse py-4">
              Loading telemetry…
            </div>
          )}
          {METRIC_DEFS.map(md => (
            <MagnitudeCard
              key={md.key}
              magnitude={{ key: md.key, title: md.metric, unit: ` ${md.unit}`, color: md.color,
                yFloor: md.key === 'temp' ? -40 : md.key === 'hum' ? 0 : -100,
                yCeil:  md.key === 'temp' ?  85 : md.key === 'hum' ? 100 : 3,
              }}
              overlays={buildOverlays(md.key)}
              group={metricGroups.find(g => g.metric === md.metric)}
              windowSec={timeWindow.sec}
              hoverTsRef={hoverTsRef}
              plotHeight={510}
            />
          ))}
        </div>
      )}

      {/* ── Table view ────────────────────────────────────────────── */}
      {hasDevices && subView === 'Table' && (
        <div className="flex-1 min-h-0 overflow-auto">
          {streamError && (
            <TelemetryErrorBanner fault={streamFault} message={streamError} />
          )}
          <MinimalisticTable<MixedTableRow>
            columns={columns}
            data={filteredTable}
            noDataMessage={isLoading ? 'Loading telemetry…' : 'No telemetry data yet.'}
            showNewButton={false}
          />
        </div>
      )}

      {/* ── Stats view ────────────────────────────────────────────── */}
      {hasDevices && subView === 'Stats' && (
        <div className="flex-1 min-h-0 overflow-y-auto pr-1">
          <DeviceStatsView
            stats={deviceStats?.devices ?? []}
            fleetTotal={deviceStats?.fleetTotal ?? 0}
            fleetTelemetry={deviceStats?.fleetTelemetry ?? 0}
            fleetEvents={deviceStats?.fleetEvents ?? 0}
            fleetStatus={deviceStats?.fleetStatus ?? 0}
            fleetDiagnostic={deviceStats?.fleetDiagnostic ?? 0}
            devices={tenantDevices}
            timeWindowLabel={timeWindow.label}
          />
        </div>
      )}
    </div>
  );
}

/* ── Device Fleet KPI Header ─────────────────────────────────────────── */

interface DeviceKpiData {
  device:      Device;
  label:       string;
  online:      boolean;
  lastSeen:    string | null;
  temperature: number | null;
  humidity:    number | null;
  rssi:        number | null;
  lastTs:      string | null;
  totalPoints: number;
  color:       string;
  /** Telemetry-only points (temp readings) — used for rate estimation. */
  telemetryPts: number;
  /** Readings per minute (extrapolated from stream window). */
  readingsPerMin: number | null;
  /** Readings per hour (extrapolated). */
  readingsPerHour: number | null;
  /** Average push interval in seconds (from temp readings). */
  avgIntervalSec: number | null;
  /** Estimated samples stored in last 24 h (extrapolated from rate). */
  est1d: number | null;
  /** Estimated samples stored in last 7 d (extrapolated from rate). */
  est1w: number | null;
}

function DeviceFleetKpi({
  devices, presenceList, snapshots, streamByDevice,
  selectedDeviceId, onSelectDevice, onRename, palette, timeWindowLabel, isSuperadmin,
}: {
  devices:          Device[];
  presenceList:     DevicePresence[];
  snapshots:        TelemetrySnapshot[];
  streamByDevice:   Record<string, Record<string, { ts: number; value: number }[]>>;
  selectedDeviceId: string | null;
  onSelectDevice:   (id: string | null) => void;
  onRename:         (deviceId: string, label: string) => void;
  palette:          string[];
  timeWindowLabel:  string;
  isSuperadmin:     boolean;
}) {
  const kpis: DeviceKpiData[] = useMemo(() => devices.map((dev, idx) => {
    const presence = presenceList.find(p => p.device_id === dev.device_id);
    const snap     = snapshots.find(s => s.scope_key === dev.device_id);
    const byDev    = streamByDevice[dev.device_id];
    const tempPts  = byDev?.temp ?? [];
    const humPts   = byDev?.hum ?? [];
    const rssiPts  = byDev?.rssi ?? [];
    const totalPts = tempPts.length + humPts.length + rssiPts.length;

    // Freshest timestamp: prefer streaming data (real-time) over snapshot
    const lastTemp = tempPts.length  ? tempPts[tempPts.length - 1]  : null;
    const lastHum  = humPts.length   ? humPts[humPts.length - 1]    : null;
    const lastRssi = rssiPts.length  ? rssiPts[rssiPts.length - 1]  : null;
    const streamFreshest = Math.max(
      lastTemp?.ts ?? 0,
      lastHum?.ts  ?? 0,
      lastRssi?.ts ?? 0,
    );
    const snapTs = snap?.last_ts ? new Date(snap.last_ts).getTime() : 0;
    const bestTs = Math.max(streamFreshest, snapTs);

    // Compute rate metrics from temperature readings (most reliable cadence signal)
    let readingsPerMin: number | null = null;
    let readingsPerHour: number | null = null;
    let avgIntervalSec: number | null = null;
    if (tempPts.length >= 2) {
      const firstTs = tempPts[0]?.ts;
      const lastTs  = tempPts[tempPts.length - 1]?.ts;
      if (typeof firstTs === 'number' && typeof lastTs === 'number') {
        const spanSec = (lastTs - firstTs) / 1000;
        if (spanSec > 0) {
          avgIntervalSec  = Math.round((spanSec / (tempPts.length - 1)) * 10) / 10;
          readingsPerMin  = Math.round((tempPts.length / spanSec) * 60 * 10) / 10;
          readingsPerHour = Math.round((tempPts.length / spanSec) * 3600);
        }
      }
    }

    return {
      device:      dev,
      label:       deviceLabel(dev),
      online:      presence?.online ?? false,
      lastSeen:    presence?.last_seen ?? dev.last_seen_at ?? null,
      temperature: snap?.temperature ?? null,
      humidity:    snap?.humidity ?? null,
      rssi:        snap?.rssi ?? null,
      lastTs:      bestTs > 0 ? new Date(bestTs).toISOString() : null,
      totalPoints: totalPts,
      color:       palette[idx % palette.length],
      telemetryPts: tempPts.length,
      readingsPerMin,
      readingsPerHour,
      avgIntervalSec,
      est1d: readingsPerHour != null ? Math.round(readingsPerHour * 24) : null,
      est1w: readingsPerHour != null ? Math.round(readingsPerHour * 24 * 7) : null,
    };
  }), [devices, presenceList, snapshots, streamByDevice, palette]);

  const onlineCount = kpis.filter(k => k.online).length;
  const totalPoints = kpis.reduce((sum, k) => sum + k.totalPoints, 0);
  const freshestTs  = kpis.reduce<string | null>((best, k) => {
    if (!k.lastTs) return best;
    if (!best) return k.lastTs;
    return k.lastTs > best ? k.lastTs : best;
  }, null);

  // Fleet-wide rate metrics
  const activeKpis = kpis.filter(k => k.readingsPerMin != null);
  const fleetRpm   = activeKpis.length > 0
    ? Math.round(activeKpis.reduce((s, k) => s + (k.readingsPerMin ?? 0), 0) * 10) / 10
    : null;
  const fleetAvgInterval = activeKpis.length > 0
    ? Math.round(activeKpis.reduce((s, k) => s + (k.avgIntervalSec ?? 0), 0) / activeKpis.length * 10) / 10
    : null;
  const fleetEst1d = activeKpis.length > 0
    ? activeKpis.reduce((s, k) => s + (k.est1d ?? 0), 0) : null;
  const fleetEst1w = activeKpis.length > 0
    ? activeKpis.reduce((s, k) => s + (k.est1w ?? 0), 0) : null;

  return (
    <div className="shrink-0 flex flex-col gap-2 max-h-[40vh] overflow-y-auto">
      {/* Fleet summary tiles */}
      <div className="flex gap-2 flex-wrap">
        <KpiTile value={String(devices.length)} label="Devices" />
        <KpiTile
          value={`${onlineCount}/${devices.length}`}
          label="Online"
          accent={onlineCount === devices.length ? 'emerald' : 'orange'}
        />
        <KpiTile
          value={fmtCompact(totalPoints)}
          label={`Readings · ${timeWindowLabel}`}
        />
        <KpiTile
          value={freshestTs ? timeAgo(freshestTs) : '—'}
          label="Latest"
          accent="emerald"
        />
        {fleetAvgInterval != null && (
          <KpiTile value={`${fleetAvgInterval}s`} label="Avg Interval" accent="emerald" />
        )}
        {fleetRpm != null && (
          <KpiTile value={String(fleetRpm)} label="Fleet r/min" accent="emerald" />
        )}
        {fleetEst1d != null && (
          <KpiTile value={`~${fmtCompact(fleetEst1d)}`} label="Est · 1d" />
        )}
        {fleetEst1w != null && (
          <KpiTile value={`~${fmtCompact(fleetEst1w)}`} label="Est · 1w" />
        )}
      </div>

      {/* Per-device cards — horizontally scrollable */}
      <div className="flex gap-2 overflow-x-auto pb-1">
        {selectedDeviceId && (
          <button
            onClick={() => onSelectDevice(null)}
            className="pac-glass rounded-lg px-3 py-2 text-[10px] uppercase tracking-wider
              text-neutral-400 hover:text-neutral-200 border border-white/[0.06]
              hover:border-white/[0.12] transition-colors shrink-0 self-center"
          >
            Show All
          </button>
        )}
        {kpis.map(k => (
          <DeviceKpiCard
            key={k.device.device_id}
            kpi={k}
            selected={selectedDeviceId === k.device.device_id}
            onClick={() => onSelectDevice(
              selectedDeviceId === k.device.device_id ? null : k.device.device_id
            )}
            onRename={(newLabel) => onRename(k.device.device_id, newLabel)}
            isSuperadmin={isSuperadmin}
          />
        ))}
      </div>
    </div>
  );
}

function DeviceKpiCard({ kpi, selected, onClick, onRename, isSuperadmin }: {
  kpi:          DeviceKpiData;
  selected:     boolean;
  onClick:      () => void;
  onRename:     (label: string) => void;
  isSuperadmin: boolean;
}) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft]     = useState(kpi.label);
  const inputRef = useRef<HTMLInputElement>(null);

  const startEdit = (e: React.MouseEvent) => {
    e.stopPropagation();
    setDraft(kpi.label);
    setEditing(true);
    // Auto-focus after React re-render
    setTimeout(() => inputRef.current?.select(), 0);
  };

  const commitEdit = () => {
    const trimmed = draft.trim();
    setEditing(false);
    if (trimmed && trimmed !== kpi.label) {
      onRename(trimmed);
    }
  };

  const cancelEdit = () => {
    setEditing(false);
    setDraft(kpi.label);
  };

  return (
    <div
      onClick={onClick}
      className={`pac-glass rounded-lg p-3 min-w-[180px] cursor-pointer shrink-0
        border transition-colors duration-150
        ${selected
          ? 'border-emerald-500/40 ring-1 ring-emerald-500/20'
          : 'border-white/[0.06] hover:border-white/[0.12]'}`}
    >
      {/* Header: online dot + label (double-click to rename) + palette swatch */}
      <div className="flex items-center gap-2 mb-1.5">
        <PresenceDot
          online={kpi.online}
          className={kpi.online ? 'shrink-0 shadow-[0_0_4px_rgba(52,211,153,0.5)]' : 'shrink-0'}
        />
        {editing ? (
          <input
            ref={inputRef}
            type="text"
            value={draft}
            onChange={e => setDraft(e.target.value)}
            onBlur={commitEdit}
            onKeyDown={e => {
              if (e.key === 'Enter')  commitEdit();
              if (e.key === 'Escape') cancelEdit();
            }}
            onClick={e => e.stopPropagation()}
            className="text-sm font-medium text-neutral-200 bg-white/[0.06] border border-white/[0.12]
              rounded px-1 py-0.5 outline-none focus:ring-1 focus:ring-sky-500 w-full min-w-0"
            maxLength={64}
          />
        ) : (
          <span className="flex items-center gap-1 min-w-0">
            <span
              className="text-sm font-medium text-neutral-200 truncate cursor-text
                hover:text-sky-400 transition-colors"
              onDoubleClick={startEdit}
              title={isSuperadmin ? 'Click pencil to rename' : 'Double-click to rename'}
            >
              {kpi.label}
            </span>
            {isSuperadmin && (
              <button
                onClick={startEdit}
                className="shrink-0 p-0.5 rounded text-neutral-500 hover:text-sky-400 transition-colors"
                title="Rename device"
              >
                <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
                </svg>
              </button>
            )}
          </span>
        )}
        <span
          className="ml-auto w-2.5 h-2.5 rounded-full shrink-0 opacity-60"
          style={{ backgroundColor: kpi.color }}
        />
      </div>

      {/* Latest readings — color-coded inline */}
      <div className="flex items-baseline gap-3 text-xs font-mono mb-1.5">
        {kpi.temperature != null && (
          <span className="text-emerald-400">{kpi.temperature.toFixed(1)}°C</span>
        )}
        {kpi.humidity != null && (
          <span className="text-sky-400">{kpi.humidity.toFixed(0)}%</span>
        )}
        {kpi.rssi != null && (
          <span className="text-orange-400">{kpi.rssi}&thinsp;dBm</span>
        )}
        {kpi.temperature == null && kpi.humidity == null && kpi.rssi == null && (
          <span className="text-neutral-600 italic">no readings</span>
        )}
      </div>

      {/* Footer: point count + freshness + rate KPI */}
      <div className="flex items-center gap-1.5 text-[10px] text-neutral-500 flex-wrap">
        <span className="tabular-nums">{fmtCompact(kpi.totalPoints)} pts</span>
        {kpi.avgIntervalSec != null && (
          <>
            <span>·</span>
            <span className="tabular-nums" title="Average push interval">⏱ {kpi.avgIntervalSec}s</span>
          </>
        )}
        {kpi.readingsPerMin != null && (
          <>
            <span>·</span>
            <span className="tabular-nums" title="Readings per minute">{kpi.readingsPerMin}/min</span>
          </>
        )}
        {kpi.readingsPerHour != null && (
          <>
            <span>·</span>
            <span className="tabular-nums" title="Readings per hour">{kpi.readingsPerHour.toLocaleString()}/hr</span>
          </>
        )}
        {kpi.est1d != null && (
          <>
            <span>·</span>
            <span className="tabular-nums" title="Estimated samples stored in last 24 h">~{fmtCompact(kpi.est1d)}/d</span>
          </>
        )}
        {kpi.est1w != null && (
          <>
            <span>·</span>
            <span className="tabular-nums" title="Estimated samples stored in last 7 d">~{fmtCompact(kpi.est1w)}/w</span>
          </>
        )}
        {kpi.lastTs && (
          <>
            <span>·</span>
            <span title={formatTsFull(kpi.lastTs)}>{timeAgo(kpi.lastTs)}</span>
          </>
        )}
      </div>
    </div>
  );
}

/* ── Device Stats View ───────────────────────────────────────────────── */

function DeviceStatsView({
  stats, fleetTotal, fleetTelemetry, fleetEvents, fleetStatus, fleetDiagnostic,
  devices, timeWindowLabel,
}: {
  stats:            DeviceStat[];
  fleetTotal:       number;
  fleetTelemetry:   number;
  fleetEvents:      number;
  fleetStatus:      number;
  fleetDiagnostic:  number;
  devices:          Device[];
  timeWindowLabel:  string;
}) {
  // Map deviceKey → label using the device list already in memory
  const labelMap = useMemo(() => {
    const m = new Map<string, string>();
    devices.forEach(d => m.set(d.device_id, deviceLabel(d)));
    return m;
  }, [devices]);

  if (stats.length === 0) {
    return (
      <div className="flex-1 grid place-items-center text-neutral-600 text-sm py-12">
        No ingestion statistics for this time window.
      </div>
    );
  }

  // Fleet aggregate
  const fleetAvgInterval = stats.length > 0
    ? stats.reduce((s, d) => s + (d.avgIntervalSec ?? 0), 0) / stats.filter(d => d.avgIntervalSec != null).length
    : null;
  const fleetRpm = stats.length > 0
    ? stats.reduce((s, d) => s + (d.requestsPerMinute ?? 0), 0)
    : null;

  return (
    <div className="flex flex-col gap-4">
      {/* Fleet summary */}
      <div className="flex gap-2 flex-wrap">
        <KpiTile value={fleetTotal.toLocaleString()} label={`Total Rows · ${timeWindowLabel}`} />
        <KpiTile value={String(stats.length)} label="Reporting Devices" />
        {fleetTelemetry > 0 && (
          <KpiTile value={fleetTelemetry.toLocaleString()} label="Telemetry" accent="emerald" />
        )}
        {fleetEvents > 0 && (
          <KpiTile value={fleetEvents.toLocaleString()} label="Events" accent="orange" />
        )}
        {fleetStatus > 0 && (
          <KpiTile value={fleetStatus.toLocaleString()} label="Status" />
        )}
        {fleetDiagnostic > 0 && (
          <KpiTile value={fleetDiagnostic.toLocaleString()} label="Diagnostic" />
        )}
        {fleetAvgInterval != null && !Number.isNaN(fleetAvgInterval) && (
          <KpiTile value={`${fleetAvgInterval.toFixed(1)}s`} label="Avg Interval" accent="emerald" />
        )}
        {fleetRpm != null && (
          <KpiTile value={fleetRpm.toFixed(1)} label="Fleet req/min" accent="emerald" />
        )}
      </div>

      {/* Per-device cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
        {stats.map(ds => (
          <DeviceStatCard
            key={ds.deviceKey}
            stat={ds}
            label={labelMap.get(ds.deviceKey) ?? ds.deviceKey}
          />
        ))}
      </div>
    </div>
  );
}

function DeviceStatCard({ stat, label }: { stat: DeviceStat; label: string }) {
  return (
    <div className="pac-glass rounded-lg p-4 border border-white/[0.06]">
      {/* Header */}
      <div className="flex items-center justify-between mb-3">
        <span className="text-sm font-medium text-neutral-200 truncate">{label}</span>
        <span className="text-lg font-semibold text-emerald-400 tabular-nums">
          {stat.totalRequests.toLocaleString()}
        </span>
      </div>

      {/* Key stats row */}
      <div className="grid grid-cols-3 gap-2 mb-3">
        <div>
          <div className="text-[10px] uppercase tracking-wider text-neutral-500">Interval</div>
          <div className="text-xs font-mono text-neutral-300">
            {stat.avgIntervalSec != null ? `${stat.avgIntervalSec}s` : '—'}
          </div>
        </div>
        <div>
          <div className="text-[10px] uppercase tracking-wider text-neutral-500">Req/min</div>
          <div className="text-xs font-mono text-neutral-300">
            {stat.requestsPerMinute != null ? stat.requestsPerMinute : '—'}
          </div>
        </div>
        <div>
          <div className="text-[10px] uppercase tracking-wider text-neutral-500">Span</div>
          <div className="text-xs font-mono text-neutral-300">
            {fmtSpan(stat.spanSec)}
          </div>
        </div>
      </div>

      {/* Category breakdown */}
      <div className="grid grid-cols-4 gap-2 mb-3">
        <div>
          <div className="text-[10px] uppercase tracking-wider text-neutral-500">Telemetry</div>
          <div className="text-xs font-mono text-emerald-400 tabular-nums">{stat.telemetryCount.toLocaleString()}</div>
        </div>
        <div>
          <div className="text-[10px] uppercase tracking-wider text-neutral-500">Events</div>
          <div className="text-xs font-mono text-orange-400 tabular-nums">{stat.eventCount.toLocaleString()}</div>
        </div>
        <div>
          <div className="text-[10px] uppercase tracking-wider text-neutral-500">Status</div>
          <div className="text-xs font-mono text-sky-400 tabular-nums">{stat.statusCount.toLocaleString()}</div>
        </div>
        <div>
          <div className="text-[10px] uppercase tracking-wider text-neutral-500">Diag</div>
          <div className="text-xs font-mono text-purple-400 tabular-nums">{stat.diagnosticCount.toLocaleString()}</div>
        </div>
      </div>

      {/* Time range */}
      <div className="flex items-center gap-2 text-[10px] text-neutral-500 mb-3">
        <span title={formatTsFull(stat.firstSeen)}>First: {timeAgo(stat.firstSeen)}</span>
        <span>→</span>
        <span title={formatTsFull(stat.lastSeen)}>Last: {timeAgo(stat.lastSeen)}</span>
      </div>

      {/* Per-metric breakdown */}
      <div className="flex gap-3 text-xs font-mono">
        {stat.byMetric.map(bm => (
          <div key={bm.metric} className="flex items-baseline gap-1">
            <span className={
              bm.metric === 'Temperature' ? 'text-emerald-400' :
              bm.metric === 'Humidity'    ? 'text-sky-400'     :
              bm.metric === 'RSSI'        ? 'text-orange-400'  :
              'text-neutral-400'
            }>
              {bm.count.toLocaleString()}
            </span>
            <span className="text-[10px] text-neutral-500">{bm.unit}</span>
          </div>
        ))}
      </div>
    </div>
  );
}
